import { useEffect, useState } from 'react';
import { collection, onSnapshot, type Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { fetchPreviewLibrary } from '../api/preview';
import { useGuest } from '../guestMode';
import type { Technique } from '../types';

function mapDoc(doc: { id: string; data(): Record<string, unknown> }): Technique {
  const d = doc.data();
  return {
    id: doc.id,
    name: d.name as string,
    japaneseName: (d.japaneseName as string) || undefined,
    aliases: Array.isArray(d.aliases) ? (d.aliases as string[]).filter((a) => typeof a === 'string' && a) : undefined,
    categoryId: d.categoryId as string,
    tier: d.tier as Technique['tier'],
    type: d.type as Technique['type'],
    uniform: (d.uniform as Technique['uniform']) || undefined,
    arts: Array.isArray(d.arts) && d.arts.length > 0 ? (d.arts as Technique['arts']) : undefined,
    instagramUrl: (d.videoUrl as string) || undefined,
    thumbnail: (d.thumbnail as string) || undefined,
    author: (d.author as string) || undefined,
    attribution: (d.attribution as string) || undefined,
    context: (d.context as string) || undefined,
    rule: (d.rule as string) || undefined,
    createdAt: (d.createdAt as Timestamp | undefined)?.toMillis?.() ?? undefined,
  };
}

interface LibraryState {
  techniques: Technique[];
  /** False once the collection has answered, however it answered. */
  loading: boolean;
}

/**
 * The catalog listener — one, shared by every hook that asks for it.
 *
 * Half a dozen components want the library at once, and Home's tabs mount and
 * unmount a rail on each click. A listener per component meant the same
 * collection target being added and dropped over and over, which the Firestore
 * SDK does not survive: it fails an internal assertion ("Unexpected state",
 * ID ca9/b815) and takes the page down with it. So the listener is opened once
 * and the mounted hooks are counted; teardown waits a tick so a remount in the
 * same tick (React's strict-mode double mount, a tab switching rails) reuses
 * the listener rather than restarting it.
 */
let library: LibraryState = { techniques: [], loading: true };
const readers = new Set<(state: LibraryState) => void>();
let unsubscribeLibrary: (() => void) | null = null;
let closing: ReturnType<typeof setTimeout> | null = null;

function publish(next: LibraryState) {
  library = next;
  for (const read of readers) read(next);
}

function subscribeLibrary(read: (state: LibraryState) => void): () => void {
  if (closing) { clearTimeout(closing); closing = null; }
  readers.add(read);
  if (!unsubscribeLibrary) {
    unsubscribeLibrary = onSnapshot(
      collection(db, 'public_techniques'),
      (snap) => publish({ techniques: snap.docs.map(mapDoc), loading: false }),
      // Denied (signed out) or offline: what we already have, and done waiting.
      () => publish({ techniques: library.techniques, loading: false }),
    );
  } else {
    read(library);
  }
  return () => {
    readers.delete(read);
    if (readers.size > 0) return;
    closing = setTimeout(() => {
      closing = null;
      if (readers.size > 0) return;
      unsubscribeLibrary?.();
      unsubscribeLibrary = null;
      library = { techniques: [], loading: true };
    }, 0);
  };
}

/**
 * The library.
 *
 * `guest` is the free preview: public_techniques is closed to signed-out
 * readers (firestore.rules), so the same index comes from a public endpoint
 * instead. Guests get every technique's name, category and tier — but no source
 * URLs, so their copies carry an opaque preview handle in `instagramUrl` that
 * only the preview endpoint can resolve. See src/api/preview.ts.
 *
 * Left unsaid, it comes from GuestContext — a signed-out listener on
 * public_techniques is denied, and mounting and unmounting denied listeners
 * (a rail per Home tab) crashes the Firestore SDK outright with an internal
 * assertion. Callers may still pass it explicitly.
 */
export function useTechniques({ guest }: { guest?: boolean } = {}) {
  const ctx = useGuest();
  const isGuest = guest ?? ctx.guest;
  const [state, setState] = useState<LibraryState>(() => (isGuest ? { techniques: [], loading: true } : library));

  useEffect(() => {
    if (isGuest) {
      let cancelled = false;
      fetchPreviewLibrary()
        .then((list) => { if (!cancelled) setState({ techniques: list, loading: false }); })
        .catch(() => { if (!cancelled) setState((prev) => ({ ...prev, loading: false })); });
      return () => { cancelled = true; };
    }
    return subscribeLibrary(setState);
  }, [isGuest]);

  return { techniques: state.techniques, loading: state.loading };
}
